// Contas Financeiras — Fase 2 (2026-07-21): lista as contas da empresa e permite
// definir o SALDO INICIAL de cada uma (a âncora do fluxo por conta). Cartões são
// passivo (fatura derivada), não têm saldo inicial. O total de caixa soma
// corrente + investimento + caixa (exclui cartões).
// 22/09/2026: "Nova conta" e "Cadastro" (ModalContaFinanceira) — antes não havia
// como criar conta; as 8 nasceram por migration. Desativada some dos totais.
import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Table, Tag, Button, Modal, DatePicker, InputNumber, Typography, Card, Space, Tooltip,
  Switch,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { usePerms } from '../hooks/usePerms';
import { ModalContaFinanceira } from './financeiro-contas/ModalContaFinanceira';

const { Text } = Typography;

type Conta = {
  id: string; nome: string; apelido: string | null; banco: string; tipo: string; ordem: number;
  id_externo_ca: string | null; extrato_banco: string | null; extrato_conta_id: string | null;
  cartao_id: string | null; ativo: boolean;
  saldo_inicial_data: string | null; saldo_inicial_valor: string | null; saldo_inicial_por: string | null;
  entradas?: string | null; saidas?: string | null; extrato_ate?: string | null; saldo_atual?: string | null;
  // FZ 5.4 (backend em rollout paralelo — campos opcionais): lançamentos do
  // sistema APÓS a última data do extrato + saldo projetado com eles.
  // Lembrar: numeric do node-pg chega string — parseFloat antes de usar.
  pos_extrato_entradas?: string | null; pos_extrato_saidas?: string | null;
  pos_extrato_n?: number | string | null; saldo_projetado?: string | null;
  // Caixa e Investimento não têm extrato próprio: o saldo sai da âncora mais o
  // que se moveu depois dela (lançamentos no caixa; aplicações/resgates no
  // investimento, que é a base do cálculo de rendimento).
  lancamentos_desde_ancora?: number | string | null;
  aplicacoes_desde_montante?: number | string | null;
  resgates_desde_montante?: number | string | null;
  // extrato_ate = até quando o extrato COBRE; ultimo_movimento = data da última
  // transação. Conta parada tem cobertura recente e movimento antigo.
  ultimo_movimento?: string | null;
  // O extrato entra sozinho pela integração do banco (hoje: conta corrente do
  // Inter) — para essa conta o upload de arquivo OFX é recusado pelo servidor.
  extrato_pela_integracao?: boolean | null;
  // Cartão: saldo_atual vem NEGATIVO (é dívida) e vale o que ainda não caiu em
  // fatura — parcelas 8/10, 9/10 de compra parcelada. A fatura já paga aparece
  // como transferência da conta corrente pro cartão.
  cartao_parcelas_a_faturar?: number | string | null;
  cartao_proxima_competencia?: string | null;
  ultima_fatura_data?: string | null;
  ultima_fatura_valor?: string | null;
  ultima_fatura_conta?: string | null;
};

const fmt = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TIPO_META: Record<string, { label: string; cor: string }> = {
  corrente:     { label: 'Conta corrente', cor: 'blue' },
  investimento: { label: 'Investimento',   cor: 'green' },
  caixa:        { label: 'Caixa',          cor: 'default' },
  cartao:       { label: 'Cartão',         cor: 'gold' },
};

export default function ContasFinanceiras() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('financeiro.contas.editar') || pode('admin.permissoes.editar');

  const [contas, setContas] = useState<Conta[]>([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState<Conta | null>(null);
  const [novaData, setNovaData] = useState<Dayjs>(dayjs());
  const [novoSaldo, setNovoSaldo] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Fase 3 — montante do investimento (calcula rendimento → receita financeira)
  const [invConta, setInvConta] = useState<Conta | null>(null);
  const [invData, setInvData] = useState<Dayjs>(dayjs());
  const [invMontante, setInvMontante] = useState<number | null>(null);
  const [invPreview, setInvPreview] = useState<any>(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invSalvando, setInvSalvando] = useState(false);

  // Cadastro (22/09/2026): 'nova' | a conta em edição | fechado.
  const [cadastro, setCadastro] = useState<'nova' | Conta | null>(null);
  const [mostrarInativas, setMostrarInativas] = useState(false);

  async function carregar(): Promise<Conta[]> {
    setLoading(true);
    try {
      const token = await getToken();
      const qs = mostrarInativas ? '?incluir_inativas=1' : '';
      const r = await fetch(`${API_URL}/financeiro/contas${qs}`, { headers: { Authorization: token } });
      const d = await r.json();
      const lista: Conta[] = Array.isArray(d) ? d : (d.body ? JSON.parse(d.body) : []);
      setContas(lista);
      return lista;
    } catch (e: any) {
      message.error('Erro ao carregar contas: ' + (e?.message || e));
      return [];
    } finally { setLoading(false); }
  }
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarInativas]);

  // Depois de cadastrar investimento, abre direto o "Informar montante" com a
  // data de HOJE: o primeiro montante é o ponto de partida — com data de antes
  // do dinheiro chegar (ou zero), o montante seguinte viraria rendimento falso.
  async function aoSalvarCadastro(salva: { id: string; tipo: string }, nova: boolean) {
    setCadastro(null);
    const lista = await carregar();
    if (nova && salva.tipo === 'investimento') {
      const c = lista.find(x => x.id === salva.id);
      if (c) abrirMontante(c);
    }
  }

  function abrirEditor(c: Conta) {
    setEditando(c);
    setNovaData(c.saldo_inicial_data ? dayjs(String(c.saldo_inicial_data).slice(0, 10)) : dayjs());
    setNovoSaldo(c.saldo_inicial_valor != null ? parseFloat(c.saldo_inicial_valor) : null);
  }

  async function salvar() {
    if (!editando) return;
    if (novoSaldo == null || isNaN(novoSaldo)) { message.warning('Informe o saldo'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/financeiro/contas/saldo-inicial`, {
        method: 'POST', headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ conta_id: editando.id, data_ref: novaData.format('YYYY-MM-DD'), saldo: novoSaldo }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.erro) { message.error(d?.erro || 'Falha ao salvar'); return; }
      message.success(`Saldo inicial de ${editando.apelido} definido`);
      setEditando(null);
      await carregar();
    } catch (e: any) {
      message.error('Erro ao salvar: ' + (e?.message || e));
    } finally { setSalvando(false); }
  }

  function abrirMontante(c: Conta) {
    setInvConta(c);
    // Data de HOJE, não a do montante anterior: informar montante é uma foto de
    // agora. (Repetir a data antiga fazia o cálculo comparar o período errado.)
    setInvData(dayjs());
    // Sugere o saldo ESPERADO SEM RENDIMENTO (montante anterior + aplicações −
    // resgates), não o montante anterior cru. Assim, se o valor do app divergir
    // muito da sugestão, a diferença salta aos olhos antes de lançar — foi o que
    // faltou em 2026-07-29, quando um montante sem o resgate de R$ 29.000
    // abatido produziria R$ 28.340,91 de "rendimento" (6% em 5 dias).
    const esperado = c.saldo_atual != null ? parseFloat(c.saldo_atual)
      : (c.saldo_inicial_valor != null ? parseFloat(c.saldo_inicial_valor) : null);
    setInvMontante(Number.isFinite(esperado as number) ? (esperado as number) : null);
    setInvPreview(null);
  }
  async function previewRendimento() {
    if (!invConta || invMontante == null) { setInvPreview(null); return; }
    setInvLoading(true);
    try {
      const token = await getToken();
      const q = new URLSearchParams({ conta_id: invConta.id, data_ref: invData.format('YYYY-MM-DD'), montante: String(invMontante) });
      const r = await fetch(`${API_URL}/financeiro/contas/rendimento-preview?${q}`, { headers: { Authorization: token } });
      const d = await r.json();
      setInvPreview(r.ok && !d?.erro ? d : null);
    } catch { setInvPreview(null); }
    finally { setInvLoading(false); }
  }
  // recalcula o preview quando muda montante/data
  useEffect(() => { if (invConta) previewRendimento(); /* eslint-disable-next-line */ }, [invMontante, invData, invConta]);

  async function lancarMontante() {
    if (!invConta || invMontante == null) { message.warning('Informe o montante'); return; }
    setInvSalvando(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/financeiro/contas/montante-investimento`, {
        method: 'POST', headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ conta_id: invConta.id, data_ref: invData.format('YYYY-MM-DD'), montante: invMontante }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.erro) { message.error(d?.erro || 'Falha ao lançar'); return; }
      if (d.primeiro) message.success('Montante inicial registrado — é o ponto de partida (sem rendimento ainda)');
      else if (d.cr_id) message.success(`Rendimento de ${fmt(d.rendimento_a_lancar ?? d.rendimento_mes ?? d.rendimento)} lançado na DRE (${d.competencia})`);
      else if (d.ja_lancado > 0) message.success('Montante registrado — o rendimento do mês já estava lançado, nada a complementar');
      else message.success('Montante registrado (rendimento próximo de zero)');
      setInvConta(null);
      await carregar();
    } catch (e: any) { message.error('Erro: ' + (e?.message || e)); }
    finally { setInvSalvando(false); }
  }

  // Desativada (só aparece com "mostrar desativadas") não entra em total nenhum.
  const ativas = useMemo(() => contas.filter(c => c.ativo !== false), [contas]);
  // Total de caixa = corrente + investimento + caixa (exclui cartões, que são passivo)
  const totalCaixa = useMemo(
    () => ativas.filter(c => c.tipo !== 'cartao')
      .reduce((s, c) => s + (c.saldo_inicial_valor != null ? parseFloat(c.saldo_inicial_valor) : 0), 0),
    [ativas]);
  const semSaldo = useMemo(
    () => ativas.filter(c => c.tipo !== 'cartao' && c.saldo_inicial_valor == null).length,
    [ativas]);
  // Fase 4 — posição atual (empresa) = soma do saldo derivado das contas (exclui cartões)
  const totalPosicao = useMemo(
    () => ativas.filter(c => c.tipo !== 'cartao' && c.saldo_atual != null)
      .reduce((s, c) => s + parseFloat(c.saldo_atual as string), 0),
    [ativas]);
  const posicaoParcial = useMemo(
    () => ativas.some(c => c.tipo !== 'cartao' && c.saldo_atual == null),
    [ativas]);
  // Posição "com lançamentos": corrente = extrato até a cobertura + lançado
  // depois (saldo_projetado do backend); caixa/investimento entram pelo
  // próprio saldo_atual (que já é por lançamento/montante).
  const totalComLanc = useMemo(
    () => ativas.filter(c => c.tipo !== 'cartao' && (c.saldo_projetado != null || c.saldo_atual != null))
      .reduce((s, c) => s + (parseFloat(String(c.saldo_projetado ?? c.saldo_atual)) || 0), 0),
    [ativas]);

  const columns = [
    {
      title: 'Conta', dataIndex: 'apelido', key: 'apelido',
      render: (_: any, c: Conta) => (
        <div style={{ opacity: c.ativo === false ? 0.55 : 1 }}>
          <div style={{ fontWeight: 600 }}>
            {c.apelido || c.nome}
            {c.ativo === false && <Tag style={{ marginLeft: 6 }}>desativada</Tag>}
          </div>
          <div style={{ fontSize: 11, color: '#888' }}>{c.nome}</div>
        </div>
      ),
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 140,
      render: (t: string) => <Tag color={TIPO_META[t]?.cor}>{TIPO_META[t]?.label || t}</Tag>,
    },
    {
      title: 'Vínculo', key: 'vinculo', width: 200,
      render: (_: any, c: Conta) => c.extrato_banco
        ? <Tooltip title="Movimenta pelo extrato bancário"><Text code>{c.extrato_banco}/{c.extrato_conta_id}</Text></Tooltip>
        : c.tipo === 'cartao'
          ? <Text type="secondary">fatura (módulo cartões)</Text>
          : c.id_externo_ca
            ? <Tooltip title="Saldo/montante informado (Conta Azul)"><Text type="secondary">manual / CA</Text></Tooltip>
            : <Text type="secondary">manual</Text>,
    },
    {
      title: 'Saldo inicial', key: 'saldo', align: 'right' as const, width: 200,
      render: (_: any, c: Conta) => {
        if (c.tipo === 'cartao') return <Text type="secondary">—</Text>;
        if (c.saldo_inicial_valor == null) return <Text type="warning">não definido</Text>;
        return (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 600 }}>{fmt(c.saldo_inicial_valor)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>em {dayjs(String(c.saldo_inicial_data).slice(0, 10)).format('DD/MM/YYYY')}</div>
          </div>
        );
      },
    },
    {
      title: 'Saldo atual', key: 'atual', align: 'right' as const, width: 150,
      render: (_: any, c: Conta) => {
        // Cartão: dívida a vencer (o que ainda não caiu em fatura), não caixa.
        if (c.tipo === 'cartao') {
          const aFaturar = c.saldo_atual != null ? Math.abs(parseFloat(c.saldo_atual)) : 0;
          const nParc = parseFloat(String(c.cartao_parcelas_a_faturar ?? '0')) || 0;
          if (!aFaturar && !nParc) return <Text type="secondary">sem parcelas em aberto</Text>;
          return (
            <div style={{ textAlign: 'right' }}
              title={'Compras já lançadas no cartão que nenhuma fatura pagou ainda (parcelamentos). Não entra no total de caixa — é passivo.'
                + (c.ultima_fatura_data
                  ? `\nÚltima fatura: ${dayjs(String(c.ultima_fatura_data).slice(0, 10)).format('DD/MM')} · ${fmt(c.ultima_fatura_valor)} (saída da ${c.ultima_fatura_conta || 'conta corrente'})`
                  : '')}>
              <div style={{ fontWeight: 700, color: '#cf1322' }}>−{fmt(aFaturar)}</div>
              <div style={{ fontSize: 11, color: '#888' }}>
                a faturar · {nParc} parcela{nParc !== 1 ? 's' : ''}
              </div>
            </div>
          );
        }
        if (c.saldo_atual == null) return <Text type="secondary">—</Text>;
        const ent = parseFloat(c.entradas || '0'), sai = parseFloat(c.saidas || '0');
        const extratoAte = c.extrato_banco && c.extrato_ate
          ? dayjs(String(c.extrato_ate).slice(0, 10)) : null;
        // Extrato parado há mais de 7 dias = hora de importar OFX novo — menos na
        // conta que entra pela integração do banco: lá o arquivo é recusado, e
        // extrato velho quer dizer que a integração parou.
        const extratoVelho = !!extratoAte && dayjs().diff(extratoAte, 'day') > 7;
        const parada = !!(c.ultimo_movimento && extratoAte
          && dayjs(String(c.ultimo_movimento).slice(0, 10)).isBefore(extratoAte, 'day'));
        // Uma linha de contexto por conta — o resto vive no tooltip.
        const nota = extratoAte ? `extrato até ${extratoAte.format('DD/MM')}`
          : c.tipo === 'investimento' ? 'montante informado'
          : c.tipo === 'caixa' ? 'saldo informado'
          : null;
        const apl = parseFloat(String(c.aplicacoes_desde_montante ?? '0')) || 0;
        const res = parseFloat(String(c.resgates_desde_montante ?? '0')) || 0;
        const nLanc = parseFloat(String(c.lancamentos_desde_ancora ?? '0')) || 0;
        const dica = extratoAte
          ? [`Entradas ${fmt(ent)} · Saídas ${fmt(sai)} desde a âncora`,
             extratoVelho
               ? (c.extrato_pela_integracao
                 ? 'Extrato com mais de 7 dias — a integração com o banco está atrasada; avise o administrador'
                 : 'Extrato com mais de 7 dias — importe o OFX recente')
               : null,
             parada ? `Coberto pelo extrato; sem movimento desde ${dayjs(String(c.ultimo_movimento).slice(0, 10)).format('DD/MM')}` : null,
            ].filter(Boolean).join('\n')
          : c.tipo === 'investimento'
            ? (apl || res
              ? `Montante + movimento desde então: +${fmt(apl)} aplicações / −${fmt(res)} resgates.\nO rendimento é a diferença entre o novo montante e este valor.`
              : 'Montante informado pela Marina — o rendimento é calculado sobre ele.')
            : c.tipo === 'caixa'
              ? `Saldo informado (âncora)${nLanc > 0 ? ` + ${nLanc} lançamento${nLanc !== 1 ? 's' : ''}` : ''}`
              : undefined;
        return (
          <div style={{ textAlign: 'right' }} title={dica}>
            <div style={{ fontWeight: 700 }}>{fmt(c.saldo_atual)}</div>
            {nota && (
              <div style={{ fontSize: 11, color: extratoVelho ? '#faad14' : '#888',
                fontWeight: extratoVelho ? 600 : 400 }}>
                {nota}
              </div>
            )}
          </div>
        );
      },
    },
    {
      // Posição fresca sem esperar OFX: extrato até a cobertura + o que a
      // equipe lançou depois (por baixa, transferências incluídas). Coluna
      // própria — empilhar na do saldo deixava a célula ilegível.
      title: 'Com lançamentos (hoje)', key: 'projetado', align: 'right' as const, width: 165,
      render: (_: any, c: Conta) => {
        if (c.tipo === 'cartao' || c.saldo_atual == null) return <Text type="secondary">—</Text>;
        const posN = parseFloat(String(c.pos_extrato_n ?? '0')) || 0;
        const posEnt = parseFloat(c.pos_extrato_entradas || '0') || 0;
        const posSai = parseFloat(c.pos_extrato_saidas || '0') || 0;
        const projetado = c.saldo_projetado != null
          ? parseFloat(c.saldo_projetado)
          : parseFloat(c.saldo_atual) + posEnt - posSai;
        const novidade = posN > 0;
        return (
          <div style={{ textAlign: 'right' }}
            title={novidade
              ? `${posN} lançamento${posN !== 1 ? 's' : ''} depois da cobertura do extrato: +${fmt(posEnt)} / −${fmt(posSai)} (transferências incluídas)`
              : c.extrato_banco
                ? 'Nada lançado depois da cobertura do extrato — igual ao saldo atual'
                : 'Conta sem extrato: o saldo já vem dos lançamentos'}>
            <div style={{ fontWeight: novidade ? 700 : 400, color: novidade ? '#020D7F' : '#888' }}>
              {fmt(projetado)}
            </div>
            {novidade && (
              <div style={{ fontSize: 11, color: '#020D7F' }}>
                +{posN} lançamento{posN !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '', key: 'acao', width: 250, align: 'right' as const,
      render: (_: any, c: Conta) => {
        const btnCadastro = (
          <Tooltip title="Nome, apelido, extrato e ativar/desativar">
            <Button size="small" type="text" disabled={!podeEditar} onClick={() => setCadastro(c)}>✎ Cadastro</Button>
          </Tooltip>
        );
        if (c.tipo === 'cartao' || c.ativo === false) return btnCadastro;
        if (c.tipo === 'investimento') {
          return <Space size={4}>{btnCadastro}<Button size="small" type="primary" ghost disabled={!podeEditar} onClick={() => abrirMontante(c)}>Informar montante</Button></Space>;
        }
        return (
          <Space size={4}>
            {btnCadastro}
            <Button size="small" disabled={!podeEditar} onClick={() => abrirEditor(c)}>
              {c.saldo_inicial_valor == null ? 'Definir' : 'Editar'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Card size="small" style={{ minWidth: 250 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Posição atual da empresa (soma das contas)</Text>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0a7f4a' }}>{fmt(totalPosicao)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            saldo inicial + movimentos do extrato · cartões à parte (passivo)
            {posicaoParcial && <span style={{ color: '#B07500' }}> · algumas contas sem saldo inicial</span>}
          </Text>
        </Card>
        <Card size="small" style={{ minWidth: 250 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Posição com lançamentos (hoje)</Text>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#020D7F' }}>{fmt(totalComLanc)}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            extrato + o que foi lançado depois da cobertura — sem esperar OFX
          </Text>
        </Card>
        <Card size="small" style={{ minWidth: 230 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Saldo inicial (âncora)</Text>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#020D7F' }}>{fmt(totalCaixa)}</div>
          {semSaldo > 0 && <Text type="warning" style={{ fontSize: 11 }}>{semSaldo} conta(s) sem saldo inicial</Text>}
        </Card>
        <Space direction="vertical" size={6} style={{ marginLeft: 8 }}>
          <Button type="primary" disabled={!podeEditar} onClick={() => setCadastro('nova')}>+ Nova conta</Button>
          <span style={{ fontSize: 12, color: '#666' }}>
            <Switch size="small" checked={mostrarInativas} onChange={setMostrarInativas} />{' '}mostrar desativadas
          </span>
        </Space>
      </Space>
      <Table
        rowKey="id" size="small" loading={loading} pagination={false}
        dataSource={contas} columns={columns}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
        O saldo inicial é a âncora do fluxo de cada conta. As correntes andam pelo extrato a partir dele;
        o investimento parte deste ponto e valoriza pelo que a Marina informar. Cartões são passivo (fatura).
      </Text>

      {cadastro && (
        <ModalContaFinanceira
          conta={cadastro === 'nova' ? null : cadastro}
          onFechar={() => setCadastro(null)}
          onSalvou={aoSalvarCadastro}
        />
      )}

      <Modal
        open={!!editando}
        title={editando ? `Saldo inicial — ${editando.apelido || editando.nome}` : ''}
        onCancel={() => setEditando(null)}
        onOk={salvar}
        confirmLoading={salvando}
        okText="Salvar"
        okButtonProps={{ disabled: !podeEditar }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text type="secondary">Data de referência (a partir de quando esse saldo vale)</Text>
            <DatePicker value={novaData} onChange={(d) => d && setNovaData(d)} format="DD/MM/YYYY" style={{ width: '100%' }} allowClear={false} />
          </div>
          <div>
            <Text type="secondary">Saldo (R$)</Text>
            <InputNumber
              value={novoSaldo} onChange={(v) => setNovoSaldo(v as number)}
              style={{ width: '100%' }} decimalSeparator="," precision={2} step={0.01}
            />
          </div>
          {editando?.tipo === 'investimento' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              É o montante líquido do investimento nesta data. A valorização depois vira receita financeira (Fase 3).
            </Text>
          )}
        </Space>
      </Modal>

      <Modal
        open={!!invConta}
        title={invConta ? `Montante do investimento — ${invConta.apelido || invConta.nome}` : ''}
        onCancel={() => setInvConta(null)}
        onOk={lancarMontante}
        confirmLoading={invSalvando}
        okText={invPreview && !invPreview.primeiro ? 'Lançar rendimento' : 'Registrar montante'}
        okButtonProps={{ disabled: !podeEditar }}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary" style={{ fontSize: 12 }}>
            Informe o montante líquido do investimento nesta data. O sistema desconta as aplicações e
            resgates do período — as linhas de CDB do extrato do banco e as transferências lançadas para
            esta conta — e lança o rendimento como receita financeira na competência.
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Text type="secondary">Data de referência</Text>
              <DatePicker value={invData} onChange={(d) => d && setInvData(d)} format="DD/MM/YYYY" style={{ width: '100%' }} allowClear={false} />
            </div>
            <div>
              <Text type="secondary">Montante líquido (R$)</Text>
              <InputNumber value={invMontante} onChange={(v) => setInvMontante(v as number)}
                style={{ width: '100%' }} decimalSeparator="," precision={2} step={0.01} />
            </div>
          </div>
          {invLoading && <Text type="secondary">Calculando rendimento…</Text>}
          {!invLoading && invPreview?.primeiro && (
            <Card size="small" style={{ background: '#F0F6FF' }}>
              <Text>É o primeiro montante desta conta — vira o <b>ponto de partida</b>. Nenhum rendimento é
                lançado agora; a partir da próxima informação o sistema calcula a valorização.</Text>
            </Card>
          )}
          {!invLoading && invPreview && !invPreview.primeiro && (
            <Card size="small" style={{ background: '#F6FFF9' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 5, fontSize: 13 }}>
                <span>Montante anterior <span style={{ color: '#888' }}>({dayjs(invPreview.montante_anterior_data).format('DD/MM/YY')})</span></span>
                <b style={{ textAlign: 'right' }}>{fmt(invPreview.montante_anterior)}</b>
                <span>− Aplicações no período</span><span style={{ textAlign: 'right' }}>{fmt(invPreview.aplicacoes)}</span>
                <span>+ Resgates no período</span><span style={{ textAlign: 'right' }}>{fmt(invPreview.resgates)}</span>
                <span>Montante novo</span><b style={{ textAlign: 'right' }}>{fmt(invMontante)}</b>
                <span style={{ borderTop: '1px solid #cfe8d8', paddingTop: 5, fontWeight: 700, color: '#0a7f4a' }}>
                  = Rendimento {invPreview.acumula_mes ? 'desta janela' : `(${invPreview.competencia})`}
                </span>
                <b style={{ borderTop: '1px solid #cfe8d8', paddingTop: 5, textAlign: 'right', color: '#0a7f4a', fontSize: 15 }}>{fmt(invPreview.rendimento)}</b>
                {/* Já houve montante informado neste mês: o que vai pra DRE é o
                    acumulado do mês, não só esta janela. */}
                {invPreview.acumula_mes && (
                  <>
                    <span style={{ fontWeight: 700, color: '#0a7f4a' }}>
                      = Total do mês ({invPreview.competencia})
                      <span style={{ color: '#888', fontWeight: 400 }}> desde {dayjs(invPreview.base_mes_data).format('DD/MM')}</span>
                    </span>
                    <b style={{ textAlign: 'right', color: '#0a7f4a', fontSize: 15 }}>{fmt(invPreview.rendimento_mes)}</b>
                  </>
                )}
              </div>
              {invPreview.acumula_mes && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                  Não é a primeira informação do mês — o total do mês é {fmt(invPreview.rendimento_mes)},
                  substituindo o lançamento anterior desta competência. Pode informar quantas vezes quiser.
                </Text>
              )}
              {/* Rendimento já lançado por outra fonte (Conta Azul) na mesma
                  competência: só o complemento é lançado, senão duplicaria. */}
              {invPreview.ja_lancado > 0 && (
                <div style={{ marginTop: 8, padding: 8, background: '#F0F6FF',
                  border: '1px solid #C4D2FF', borderRadius: 4 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 3, fontSize: 12 }}>
                    <span>Rendimento do mês (pelo saldo)</span>
                    <span style={{ textAlign: 'right' }}>{fmt(invPreview.rendimento_mes)}</span>
                    <span>− já lançado no mês <span style={{ color: '#888' }}>({invPreview.ja_lancado_n} lanç. do Conta Azul)</span></span>
                    <span style={{ textAlign: 'right' }}>{fmt(invPreview.ja_lancado)}</span>
                    <b style={{ borderTop: '1px solid #C4D2FF', paddingTop: 3, color: '#0930D0' }}>= será lançado agora</b>
                    <b style={{ borderTop: '1px solid #C4D2FF', paddingTop: 3, textAlign: 'right', color: '#0930D0' }}>
                      {fmt(invPreview.rendimento_a_lancar)}
                    </b>
                  </div>
                </div>
              )}
              {invPreview.rendimento < 0 && (
                <Text type="warning" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                  Rendimento negativo — normal quando houve resgate no período (o IR retido sai do
                  montante e não aparece no extrato da conta corrente). Se não houve resgate, confira
                  o montante antes de lançar.
                </Text>
              )}
              {/* Rendimento implausível: CDB/DI rende ~1%/mês, então acima de 3%
                  do montante quase sempre é montante informado sem descontar um
                  resgate. Caso real 2026-07-29: R$ 28.340,91 = 6% em 5 dias. */}
              {invPreview.rendimento > 0 && invPreview.montante_anterior > 0
                && invPreview.rendimento > invPreview.montante_anterior * 0.03 && (
                <div style={{ marginTop: 8, padding: 8, background: '#FFF4E5',
                  border: '1px solid #FFD591', borderRadius: 4 }}>
                  <Text style={{ fontSize: 11, color: '#7A4A00' }}>
                    ⚠ Rendimento de <b>{((invPreview.rendimento / invPreview.montante_anterior) * 100).toFixed(1)}%</b> sobre
                    o montante anterior — muito acima do esperado para aplicação de renda fixa.
                    O montante informado provavelmente ainda não desconta um resgate do período.
                    Confira o saldo no app do banco antes de lançar.
                  </Text>
                </div>
              )}
            </Card>
          )}
        </Space>
      </Modal>
    </div>
  );
}
